import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';

const AboutPage: React.FC = () => {
  const { t } = useTranslation();
  const { locale } = useParams<{ locale?: string }>();
  const currentLocale = locale || 'en';

  const techStack = t('about.techStack', { returnObjects: true }) as string[];
  const coreFeaturesList = t('about.coreFeaturesList', { returnObjects: true }) as string[];
  const designPrinciples = t('about.designPrinciples', { returnObjects: true }) as Array<{ title: string; description: string }>;

  return (
    <main style={{ background: 'var(--v2-bg)', color: 'var(--v2-ink)', minHeight: '100vh' }}>

      {/* Hero */}
      <section style={{ maxWidth: '680px', margin: '0 auto', padding: '72px 24px 48px', textAlign: 'center' }}>
        <h1 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 800, lineHeight: 1.15, marginBottom: '16px', color: 'var(--v2-ink)' }}>
          {t('about.title', 'About Notely')}
        </h1>
        <p style={{ fontSize: '1.1rem', color: 'var(--v2-ink-muted)', lineHeight: 1.6 }}>
          {t('about.subtitle', "We're building the voice note app we always wanted.")}
        </p>
      </section>

      {/* Story */}
      <section style={{ background: 'var(--v2-bg-subtle)', borderTop: '1px solid var(--v2-line)', borderBottom: '1px solid var(--v2-line)', padding: '48px 24px' }}>
        <div style={{ maxWidth: '680px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '12px', color: 'var(--v2-ink)' }}>
            {t('about.storyTitle', 'Our story')}
          </h2>
          <p style={{ fontSize: '1rem', color: 'var(--v2-ink-muted)', lineHeight: 1.75 }}>
            {t('about.storyText', 'Notely started as a personal tool to help with meeting notes and grew into a full transcription app. It began as a React web prototype, evolved through a Chrome extension, and became a cross-platform Flutter app — built to be accurate, private, and simple.')}
          </p>
        </div>
      </section>

      {/* Tech + Features — two column */}
      <section style={{ maxWidth: '960px', margin: '0 auto', padding: '56px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '48px' }}>

          {/* Tech stack */}
          <div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '16px', color: 'var(--v2-ink)' }}>
              {t('about.techTitle', 'Technical Implementation')}
            </h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {Array.isArray(techStack) && techStack.map((item, i) => (
                <li key={i} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'flex-start' }}>
                  <span style={{ color: 'var(--v2-primary)', fontWeight: 700, flexShrink: 0 }}>→</span>
                  <span style={{ fontSize: '0.95rem', color: 'var(--v2-ink-muted)', lineHeight: 1.55 }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Core features */}
          <div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '16px', color: 'var(--v2-ink)' }}>
              {t('about.coreFeaturesTitle', 'Core Features')}
            </h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {Array.isArray(coreFeaturesList) && coreFeaturesList.map((item, i) => (
                <li key={i} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'flex-start' }}>
                  <span style={{ color: 'var(--v2-primary)', fontWeight: 700, flexShrink: 0 }}>✓</span>
                  <span style={{ fontSize: '0.95rem', color: 'var(--v2-ink-muted)', lineHeight: 1.55 }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section style={{ background: 'var(--v2-bg-subtle)', borderTop: '1px solid var(--v2-line)', borderBottom: '1px solid var(--v2-line)', padding: '56px 24px' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '32px', textAlign: 'center', color: 'var(--v2-ink)' }}>
            {t('about.designTitle', 'Design Philosophy')}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            {Array.isArray(designPrinciples) && designPrinciples.map((p, i) => (
              <div key={i} style={{ background: 'var(--v2-bg)', border: '1px solid var(--v2-line)', borderRadius: '10px', padding: '20px 18px' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: '6px', color: 'var(--v2-ink)' }}>{p.title}</h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--v2-ink-muted)', lineHeight: 1.6, margin: 0 }}>{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing — location + impact */}
      <section style={{ maxWidth: '680px', margin: '0 auto', padding: '56px 24px' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '12px', color: 'var(--v2-ink)' }}>
          {t('about.locationTitle', 'Built in Stockholm')}
        </h2>
        <p style={{ fontSize: '1rem', color: 'var(--v2-ink-muted)', lineHeight: 1.75 }}>
          {t('about.locationBuiltBy', 'Built by')}{' '}
          <a
            href="https://cstate.se"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--v2-primary)', textDecoration: 'none', fontWeight: 600 }}
          >
            Christopher State
          </a>
          {' — '}
          {t('about.locationText', 'Design Engineer based in Stockholm, working at the intersection of design and code.')}
        </p>
      </section>

      {/* CTA */}
      <section style={{ padding: '0 24px 80px', textAlign: 'center' }}>
        <Link
          to={`/${currentLocale}/download`}
          style={{ display: 'inline-block', background: 'var(--v2-primary)', color: '#fff', padding: '13px 32px', borderRadius: '8px', fontWeight: 700, fontSize: '1rem', textDecoration: 'none' }}
        >
          {t('about.ctaButton', 'Download Free')}
        </Link>
      </section>

    </main>
  );
};

export default AboutPage;
